// SCR-STAFF-710 (AP-MED-04؛ US-MED-086): پایش **همه** ارسال‌های ملی نسخه، آزمایش، تصویربرداری و خدمت با وضعیت، کد
// رهگیری (کپی با یک کلیک)، بیمه‌گر، نوع، پزشک و زمان؛ صافی بیمه‌گر، نوع، وضعیت، پزشک و بازه تاریخ (پیش‌فرض ۳۰ روز).
// دامنه دید در سرور اعمال می‌شود: پزشک ارسال‌های خودش با اقلام و نقش عملیاتی کل مرکز بدون اقلام (BR-MED-087).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { unwrap, type MedNationalEntityType, type MedRxMonitorRowView, type MedRxMonitorStatus } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, ListPage, StatusChip, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { CALL_LOG_PATH, highVolumeKeys, monitorTone, SCR_STAFF_710 } from './highVolume';

const STATUSES: readonly NonNullable<MedRxMonitorStatus>[] = ['NotSent', 'Pending', 'AwaitingCredential', 'AwaitingProviderAction', 'Registered', 'Dispensed', 'Failed', 'Voided'];
const TYPES: readonly NonNullable<MedNationalEntityType>[] = ['Rx', 'Lab', 'Imaging', 'Service', 'Visit'];

export function RxMonitorPage() {
  const { t } = useTranslation('med');
  const { tenantApi, session } = useTenantSession();
  const { timeZone } = useDisplaySettings();
  const access = useAccess();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [params] = useSearchParams();

  // AC-MED-086.3 (سازگاری مسیر): SCR-STAFF-701 به صافی «ناموفق» همین صفحه هدایت می‌شود.
  const [status, setStatus] = useState<MedRxMonitorStatus | ''>((params.get('status') as MedRxMonitorStatus | null) ?? '');
  const [entityType, setEntityType] = useState<MedNationalEntityType | ''>('');
  const [insurer, setInsurer] = useState('');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const filterKey = `${status}|${entityType}|${insurer}|${remoteOnly}`;

  const rows = useQuery({
    queryKey: highVolumeKeys.monitor(session?.tenantId, filterKey),
    enabled: Boolean(session?.tenantId) && access.can('med.rx_monitor.view'),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/medical-orders/rx-monitor', {
          params: {
            query: {
              status: status || undefined,
              entityType: entityType || undefined,
              insurer: insurer || undefined,
              remoteApprovalOnly: remoteOnly || undefined,
            },
          },
        }),
      ),
  });

  const resend = useMutation({
    mutationFn: (submissionId: string) =>
      unwrap(
        tenantApi.POST('/api/v1/medical-orders/rx-monitor/{submissionId}:resend', {
          params: { path: { submissionId } },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => {
      idempotency.settle(error ?? undefined);
      void queryClient.invalidateQueries({ queryKey: highVolumeKeys.monitor(session?.tenantId, filterKey) });
    },
  });

  const canResend = access.can('med.rx_monitor.resend');
  const columns: DataTableColumn<MedRxMonitorRowView>[] = [
    { id: 'status', headerKey: 'med:rx_monitor.status', cell: (row) => <StatusChip tone={monitorTone(row.status)} labelKey={`med:rx_monitor_status.${row.status}`} /> },
    { id: 'type', headerKey: 'med:rx_monitor.type', cell: (row) => <span>{t(`med:national_entity.${row.entityType}`)}</span> },
    { id: 'insurer', headerKey: 'med:rx_monitor.insurer', cell: (row) => <span>{row.insurer}</span> },
    {
      id: 'tracking',
      headerKey: 'med:rx_monitor.tracking_code',
      cell: (row) =>
        row.trackingCode ? (
          <Tooltip title={`${row.errorCode ?? ''} ${t(`med:national_reason.${row.failureReason}`)}`.trim()}>
            <Button
              size="small"
              variant="text"
              onClick={() => void navigator.clipboard?.writeText(row.trackingCode ?? '')}
              data-testid={`med-monitor-tracking-${row.submissionId ?? row.prescriptionId ?? row.orderId}`}
            >
              {row.trackingCode}
            </Button>
          </Tooltip>
        ) : (
          <span>—</span>
        ),
    },
    { id: 'provider', headerKey: 'med:rx_monitor.provider', cell: (row) => <span>{row.providerName ?? '—'}</span> },
    { id: 'patient', headerKey: 'med:rx_monitor.patient', cell: (row) => <span>{row.patientName ?? '—'}</span> },
    { id: 'attempts', headerKey: 'med:rx_monitor.attempts', cell: (row) => <span>{faDigits(row.attempts)}</span> },
    { id: 'created', headerKey: 'med:rx_monitor.created_at', cell: (row) => <span>{toJalaliDateTime(row.createdAt, { timeZone })}</span> },
    {
      id: 'items',
      headerKey: 'med:rx_monitor.items',
      cell: (row) => <span data-testid="med-monitor-items">{(row.items ?? []).join(t('common:list_separator')) || '—'}</span>,
    },
    {
      id: 'actions',
      headerKey: 'med:rx_monitor.actions',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {canResend && row.submissionId && (row.status === 'Failed' || row.status === 'NotSent' || row.status === 'AwaitingProviderAction') ? (
            <Button size="small" variant="outlined" onClick={() => resend.mutate(row.submissionId as string)} data-testid={`med-monitor-resend-${row.submissionId}`}>
              {/* AC-MED-086.4: در خطای استحقاق، ارسال مجدد خودش استحقاق را پیش از ارسال تازه می‌کند (BR-MED-089). */}
              {t(row.failureReason === 'NotEligible' ? 'med:rx_monitor.refresh_eligibility_and_resend' : 'med:rx_monitor.resend')}
            </Button>
          ) : null}
          <Button size="small" variant="text" component={RouterLink} to={CALL_LOG_PATH} data-testid="med-monitor-calls">
            {t('med:rx_monitor.call_details')}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_710}
      titleKey="med:rx_monitor.title"
      subtitleKey="med:rx_monitor.subtitle"
      filters={
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <TextField select size="small" label={t('med:rx_monitor.status')} value={status} onChange={(event) => setStatus(event.target.value as MedRxMonitorStatus | '')} sx={{ minWidth: 200 }} slotProps={{ htmlInput: { 'data-testid': 'med-monitor-status' } }}>
            <MenuItem value="">{t('med:rx_monitor.all')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`med:rx_monitor_status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label={t('med:rx_monitor.type')} value={entityType} onChange={(event) => setEntityType(event.target.value as MedNationalEntityType | '')} sx={{ minWidth: 180 }} slotProps={{ htmlInput: { 'data-testid': 'med-monitor-type' } }}>
            <MenuItem value="">{t('med:rx_monitor.all')}</MenuItem>
            {TYPES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`med:national_entity.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" label={t('med:rx_monitor.insurer')} value={insurer} onChange={(event) => setInsurer(event.target.value.toUpperCase())} sx={{ minWidth: 160 }} slotProps={{ htmlInput: { maxLength: 20, 'data-testid': 'med-monitor-insurer' } }} />
          <TextField select size="small" label={t('med:rx_monitor.remote_only')} value={remoteOnly ? 'yes' : 'no'} onChange={(event) => setRemoteOnly(event.target.value === 'yes')} sx={{ minWidth: 200 }} slotProps={{ htmlInput: { 'data-testid': 'med-monitor-remote' } }}>
            <MenuItem value="no">{t('med:rx_monitor.all')}</MenuItem>
            <MenuItem value="yes">{t('med:rx_monitor.remote_approval')}</MenuItem>
          </TextField>
        </Stack>
      }
    >
      <Stack spacing={2}>
        {rows.data && !rows.data.itemsVisible ? (
          <Alert severity="info" data-testid="med-monitor-operational">
            {t('med:rx_monitor.operational_view')}
          </Alert>
        ) : null}
        <DataTable
          rows={rows.data?.items}
          columns={rows.data?.itemsVisible ? columns : columns.filter((column) => column.id !== 'items')}
          getRowId={(row) => `${row.submissionId ?? row.prescriptionId ?? row.orderId ?? row.encounterId}`}
          isLoading={rows.isLoading}
          error={rows.error}
          onRetry={() => void rows.refetch()}
          emptyTitleKey="med:rx_monitor.empty"
          captionKey="med:rx_monitor.title"
        />
      </Stack>
    </ListPage>
  );
}
